import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import { DateCell, ListCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import { PoliticalCampaign } from '../types/models'
import { getLocale } from '../utils/locale'

function formatMoney(amount: number, currency: string) {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

interface CampaignTableProps {
  campaigns: PoliticalCampaign[]
  loading?: boolean
  emptyMessage?: string
  selection?: PoliticalCampaign[]
  onSelectionChange?: (campaigns: PoliticalCampaign[]) => void
}

export function CampaignTable({ campaigns, loading, emptyMessage, selection, onSelectionChange }: CampaignTableProps) {
  const selectable = !!onSelectionChange
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<PoliticalCampaign, unknown>[]>(() => [
    ...(selectable ? [selectionColumn<PoliticalCampaign>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 220 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'politicalEntityName',
      header: t('political.sponsor'),
      meta: { minWidth: 160 },
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.politicalEntityName}
        </span>
      ),
    },
    {
      accessorKey: 'electionName',
      header: t('political.election'),
      meta: { minWidth: 160 },
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.electionName}
        </span>
      ),
    },
    {
      accessorKey: 'totalBudget',
      header: t('political.totalBudget'),
      size: 160,
      cell: ({ row }) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(row.original.totalBudget, row.original.currency)}
        </span>
      ),
    },
    {
      id: 'topics',
      header: t('political.topics'),
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.topics.length}
        </span>
      ),
    },
    {
      id: 'linkedAdvertisers',
      header: t('political.linkedAdvertisers'),
      meta: { minWidth: 160 },
      enableSorting: false,
      cell: ({ row }) => {
        const c = row.original
        return c.externalApprovals.length === 0 ? (
          <span style={{ color: 'var(--ad-text-faint)' }}>—</span>
        ) : c.externalApprovals.length > 3 ? (
          <span className="chip chip--pending">{c.externalApprovals.length}</span>
        ) : (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {c.externalApprovals.map((a) => (
              <span
                key={a.id}
                className="chip chip--pending"
              >
                {a.name}
              </span>
            ))}
          </div>
        )
      },
    },
    optionalColumn<PoliticalCampaign>({
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    }),
    optionalColumn<PoliticalCampaign>({
      accessorKey: 'description',
      header: t('common.description'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    }),
    optionalColumn<PoliticalCampaign>({
      accessorKey: 'currency',
      header: t('common.currency'),
      size: 110,
      cell: ({ row }) => <MutedCell value={row.original.currency} />,
    }),
    optionalColumn<PoliticalCampaign>({
      id: 'topicNames',
      header: t('political.topicNames'),
      enableSorting: false,
      meta: { minWidth: 180 },
      cell: ({ row }) => <ListCell values={row.original.topics?.map((topic) => topic.name)} />,
    }),
    optionalColumn<PoliticalCampaign>({
      accessorKey: 'ownerTeamName',
      header: t('common.team'),
      meta: { minWidth: 150 },
      cell: ({ row }) => <MutedCell value={row.original.ownerTeamName} />,
    }),
    optionalColumn<PoliticalCampaign>({
      accessorKey: 'networkName',
      header: t('political.networkHeader'),
      meta: { minWidth: 150 },
      cell: ({ row }) => <MutedCell value={row.original.networkName} />,
    }),
    optionalColumn<PoliticalCampaign>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<PoliticalCampaign>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ], [t, selectable])

  return (
    <DataTable
      columns={columns}
      data={campaigns}
      loading={loading}
      emptyMessage={emptyMessage ?? 'No campaigns found.'}
      columnsKey="politicalCampaigns"
      rowHref={(row) => `/political/campaigns/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
    />
  )
}
